import { useState } from "react";
import { toDatetimeLocal } from "../../utils/dateUtils";

export const APPOINTMENT_TYPES = [
  { value: "gp",             label: "GP" },
  { value: "health-visitor", label: "Health Visitor" },
  { value: "check-6week",    label: "6-Week Check" },
  { value: "check-8month",   label: "8-Month Review" },
  { value: "check-1year",    label: "1-Year Review" },
  { value: "check-2year",    label: "2-Year Review" },
  { value: "ae",             label: "A&E" },
  { value: "specialist",     label: "Specialist" },
  { value: "other",          label: "Other" },
];

const EMPTY = {
  date: "",
  appointmentType: "gp",
  practitioner: "",
  reason: "",
  weight: "",
  height: "",
  headCircumference: "",
  notes: "",
  prescriptions: "",
  followUpDate: "",
};

export default function DoctorVisitForm({ onAdd }) {
  const [form, setForm] = useState({ ...EMPTY, date: toDatetimeLocal() });

  const set = (field) => (e) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.reason.trim()) return;

    onAdd({
      date:             new Date(form.date).toISOString(),
      appointmentType:  form.appointmentType,
      practitioner:     form.practitioner.trim(),
      reason:           form.reason.trim(),
      weight:           form.weight,
      height:           form.height,
      headCircumference:form.headCircumference,
      notes:            form.notes.trim(),
      prescriptions:    form.prescriptions.trim(),
      followUpDate:     form.followUpDate,
    });

    setForm({ ...EMPTY, date: toDatetimeLocal() });
  };

  return (
    <form onSubmit={handleSubmit} className="mb-3">
      {/* Row 1 — Date / Type / Practitioner */}
      <div className="row g-2 mb-2">
        <div className="col-md-3">
          <label className="form-label">Date &amp; Time</label>
          <input
            type="datetime-local"
            className="form-control"
            value={form.date}
            onChange={set("date")}
            required
          />
        </div>
        <div className="col-md-4">
          <label className="form-label">Appointment Type</label>
          <select
            className="form-select"
            value={form.appointmentType}
            onChange={set("appointmentType")}
          >
            {APPOINTMENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>
        <div className="col-md-5">
          <label className="form-label">
            Practitioner <span className="text-muted fw-normal">(optional)</span>
          </label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. Dr Smith"
            value={form.practitioner}
            onChange={set("practitioner")}
          />
        </div>
      </div>

      {/* Row 2 — Reason */}
      <div className="row g-2 mb-2">
        <div className="col-12">
          <label className="form-label">Reason for Visit</label>
          <input
            type="text"
            className="form-control"
            placeholder="e.g. 8-month developmental check, persistent cough, rash..."
            value={form.reason}
            onChange={set("reason")}
            required
          />
        </div>
      </div>

      {/* Row 3 — Measurements */}
      <div className="row g-2 mb-2">
        <div className="col-md-3">
          <label className="form-label">
            Weight <span className="text-muted fw-normal">(kg)</span>
          </label>
          <input
            type="number"
            className="form-control"
            placeholder="e.g. 7.2"
            value={form.weight}
            onChange={set("weight")}
            step="0.01"
            min="0"
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">
            Height <span className="text-muted fw-normal">(cm)</span>
          </label>
          <input
            type="number"
            className="form-control"
            placeholder="e.g. 68"
            value={form.height}
            onChange={set("height")}
            step="0.1"
            min="0"
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">
            Head Circ. <span className="text-muted fw-normal">(cm)</span>
          </label>
          <input
            type="number"
            className="form-control"
            placeholder="e.g. 44"
            value={form.headCircumference}
            onChange={set("headCircumference")}
            step="0.1"
            min="0"
          />
        </div>
        <div className="col-md-3">
          <label className="form-label">
            Follow-up Date <span className="text-muted fw-normal">(if needed)</span>
          </label>
          <input
            type="date"
            className="form-control"
            value={form.followUpDate}
            onChange={set("followUpDate")}
          />
        </div>
      </div>

      {/* Row 4 — Notes / Prescriptions */}
      <div className="row g-2 mb-2">
        <div className="col-md-6">
          <label className="form-label">Outcome / Notes</label>
          <textarea
            className="form-control"
            rows={2}
            placeholder="Diagnosis, test results, advice given..."
            value={form.notes}
            onChange={set("notes")}
            style={{ resize: "vertical" }}
          />
        </div>
        <div className="col-md-6">
          <label className="form-label">Prescriptions / Treatments</label>
          <textarea
            className="form-control"
            rows={2}
            placeholder="e.g. Amoxicillin 125 mg, 3× daily for 5 days"
            value={form.prescriptions}
            onChange={set("prescriptions")}
            style={{ resize: "vertical" }}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end">
        <button type="submit" className="btn btn-primary">
          Log Visit
        </button>
      </div>
    </form>
  );
}
